type Props = {
  className?: string;
  variant?: 'brand' | 'white';
};

/**
 * Ver.iD inline SVG logo — extracted from the brand SVG.
 * Scaled to ~37×14px to match the Figma footer.
 *
 * `brand` variant uses `currentColor` (inherits from parent).
 * `white` variant uses white text + dark iD block (for dark backgrounds).
 */
export default function HostedVerIdLogo({
  className,
  variant = 'brand',
}: Props) {
  const isWhite = variant === 'white';
  const textFill = isWhite ? 'white' : 'currentColor';
  const blockFill = isWhite ? 'white' : 'currentColor';
  const letterFill = isWhite ? '#1e3f46' : 'white';

  return (
    <svg
      width="37"
      height="14"
      viewBox="0 0 528 198"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Ver.iD"
      role="img"
    >
      {/* ID block (rounded rect + letters) */}
      <path
        d="M319.678 14.22C319.678 6.37 325.882 0 333.528 0H440.557C488.846 0 528 40.19 528 89.78V108.45C528 158.03 488.846 198.23 440.557 198.23H333.528C325.882 198.23 319.678 191.86 319.678 184.01V14.22Z"
        fill={blockFill}
      />
      <path
        d="M347.221 165.36C347.221 167.32 348.77 168.92 350.689 168.92H368.678C370.587 168.92 372.145 167.33 372.145 165.36V75.94C372.145 73.98 370.597 72.38 368.678 72.38H350.689C348.78 72.38 347.221 73.97 347.221 75.94V165.36ZM345.468 43.4C345.468 51.6 352.091 57.79 359.678 57.79C367.266 57.79 373.694 51.59 373.694 43.4C373.694 35.21 367.071 29.01 359.678 29.01C352.286 29.01 345.468 35.21 345.468 43.4Z"
        fill={letterFill}
      />
      <path
        d="M388.634 165.36C388.634 167.32 390.183 168.92 392.092 168.92H436.135C478.775 168.92 500.582 143.73 500.582 98.96C500.582 54.19 478.775 29 436.135 29H392.092C390.183 29 388.634 30.59 388.634 32.56V165.36ZM416.626 145.93C414.717 145.93 413.159 144.34 413.159 142.37V55.53C413.159 53.57 414.707 51.97 416.626 51.97H434.576C464.36 51.97 475.464 66.36 475.464 98.95C475.464 131.54 464.37 145.93 434.576 145.93H416.626Z"
        fill={letterFill}
      />
      {/* VER text */}
      <path
        d="M128.186 21.44H106.184C105.454 21.44 104.791 21.92 104.548 22.63L65.433 136.8C65.394 136.92 65.277 137 65.151 137C65.024 137 64.917 136.92 64.868 136.8L25.549 22.63C25.306 21.92 24.653 21.44 23.913 21.44H1.736C0.528 21.44-0.31 22.67 0.109 23.84L51.905 167.27C52.158 167.97 52.801 168.43 53.531 168.43H77.637C78.368 168.43 79.02 167.96 79.264 167.26L129.813 23.83C130.222 22.67 129.384 21.45 128.186 21.45V21.44Z"
        fill={textFill}
      />
      <path
        d="M170.262 57.71C154.668 57.71 141.53 63.21 132.248 73.61C123.161 83.8 118.349 98.02 118.349 114.73C118.349 131.44 122.995 145.44 131.78 155.63C140.994 166.31 154.298 171.95 170.262 171.95C182.884 171.95 193.52 168.68 201.896 162.22C209.522 156.34 214.967 148.12 218.074 137.77C218.346 136.87 218.191 135.92 217.645 135.16C217.1 134.4 216.213 133.94 215.288 133.94H198.916C197.65 133.94 196.51 134.78 196.082 136.03C195.039 139.06 192.906 143.43 188.66 147.02C184.004 150.95 177.946 152.94 170.661 152.94C161.876 152.94 154.464 149.75 149.234 143.71C144.539 138.29 141.676 130.59 141.159 122.04C141.159 121.91 141.198 121.79 141.286 121.7C141.383 121.6 141.51 121.54 141.656 121.54H217.226C218.755 121.54 220.012 120.33 220.08 118.8C220.158 117.08 220.158 114.84 220.158 112.66C220.158 96.12 215.678 82.31 207.194 72.73C198.497 62.9 185.728 57.71 170.262 57.71ZM169.658 76.72C177.479 76.72 184.209 79.27 189.127 84.09C193.9 88.77 196.607 95.2 196.978 102.68C196.978 102.8 196.939 102.91 196.861 103C196.773 103.1 196.637 103.15 196.5 103.15H141.744C141.539 103.15 141.422 103.05 141.374 102.98C141.286 102.88 141.247 102.76 141.267 102.64C143.127 86.89 154.279 76.71 169.667 76.71L169.658 76.72Z"
        fill={textFill}
      />
      <path
        d="M294.637 54.28C293.985 54.27 293.342 54.25 292.679 54.23C290.469 54.17 288.384 54.11 286.017 54.3C282.375 54.51 278.752 55.23 275.255 56.42C271.798 57.56 268.613 59.21 265.788 61.33C262.847 63.35 260.577 65.81 258.133 68.58C257.957 68.78 257.792 68.98 257.626 69.17V57.83C257.626 56.16 256.311 54.81 254.685 54.81H239.433C237.816 54.81 236.491 56.16 236.491 57.83V165.82C236.491 167.49 237.806 168.84 239.433 168.84H257.198C258.824 168.84 260.139 167.49 260.139 165.82V111.31C260.139 107.02 260.568 102.9 261.415 99.08C262.253 95.24 263.626 91.72 265.486 88.63C267.395 85.56 270.015 82.91 273.268 80.74C276.706 78.45 280.515 77.09 284.596 76.7C288.413 76.34 291.774 76.3 294.627 76.29C296.244 76.29 297.569 74.93 297.569 73.27V57.31C297.569 55.67 296.264 54.3 294.647 54.27L294.637 54.28Z"
        fill={textFill}
      />
    </svg>
  );
}
